import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Row,
  Col,
  Typography,
  Input,
  App,
  Tag,
} from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRecurringJournals,
  deleteRecurringJournal,
} from "./recurringJournalThunk";
import {
  resetRecurringJournals,
  setSelectedRecurring,
  setIsEditRecurring,
  setRefreshRecurringList,
} from "./recurringJournalSlice";
import { getAccessType } from "../../lib/functions";
import commaNumber from "comma-number";

const { Title } = Typography;
const format = commaNumber.bindWith(",", ".");

const frequencyLabel = (frequency, interval) => {
  if (interval === 1) {
    const map = {
      day:   "Daily",
      week:  "Weekly",
      month: "Monthly",
      year:  "Yearly",
    };
    return map[frequency] || frequency;
  }
  return `Every ${interval} ${frequency}${interval > 1 ? "s" : ""}`;
};

const RecurringJournalList = () => {
  const { modal }   = App.useApp();
  const navigate    = useNavigate();
  const dispatch    = useDispatch();
  const { list, loading, refreshList } = useSelector(
    (state) => state.recurringJournals
  );

  const [searchText, setSearchText] = useState("");
  const [pageSize,   setPageSize]   = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [access_type, setAccessType]  = useState("");

  useEffect(() => {
    const access = getAccessType("journal_entry");
    setAccessType(access);
  }, []);

  useEffect(() => {
    dispatch(resetRecurringJournals());
    dispatch(fetchRecurringJournals());
  }, [dispatch]);

  useEffect(() => {
    if (refreshList) {
      dispatch(fetchRecurringJournals());
      dispatch(setRefreshRecurringList(false));
    }
  }, [refreshList, dispatch]);

  const filtered = useMemo(() => {
    if (!searchText.trim()) return list;
    return list.filter((r) =>
      Object.values(r || {})
        .join(" ")
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
  }, [list, searchText]);

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteRecurringJournal(id)).unwrap();
      modal.success({ title: "Recurring journal deleted successfully." });
      dispatch(fetchRecurringJournals());
    } catch (error) {
      modal.warning({ title: error.message || "Delete failed." });
    }
  };

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Narration",
      dataIndex: "narration",
    },
    {
      title: "Frequency",
      render: (_, record) =>
        frequencyLabel(record.frequency, record.interval_value),
      sorter: (a, b) => a.frequency.localeCompare(b.frequency),
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      render: (val) => val?.slice(0, 10),
      sorter: (a, b) =>
        new Date(a.start_date).getTime() - new Date(b.start_date).getTime(),
    },
    {
      title: "Next Run",
      dataIndex: "next_run_date",
      render: (val) => val?.slice(0, 10),
      sorter: (a, b) =>
        new Date(a.next_run_date).getTime() -
        new Date(b.next_run_date).getTime(),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      render: (val) => (val ? val.slice(0, 10) : <Tag>No end</Tag>),
    },
    {
      title: "Debit Total",
      align: "right",
      render: (_, record) => {
        const rows = record.rows_json || [];
        const total = rows.reduce(
          (s, r) => s + (Number(r.debit_amount) || 0),
          0
        );
        return format(total.toFixed(2));
      },
    },
    {
      title: "Created By",
      dataIndex: "full_name",
    },
    {
      title: "Actions",
      render: (_, record) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              dispatch(setSelectedRecurring(record));
              dispatch(setIsEditRecurring(true));
              navigate("/accounts/recurring-journals/add");
            }}
          />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              modal.confirm({
                title: "Are you sure?",
                content: `This will delete the recurring journal: ${record.narration}`,
                okText: "Yes, Delete",
                cancelText: "Cancel",
                onOk: () => handleDelete(record.id),
              });
            }}
          />
        </>
      ),
    },
  ];

  const detailColumns = [
    {
      title: "Sl No.",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Account",
      dataIndex: "acc_name",
    },
    {
      title: "Debit Amount",
      dataIndex: "debit_amount",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
    {
      title: "Credit Amount",
      dataIndex: "credit_amount",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
  ];

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
          onClick={() => navigate(-1)}
          data-testid="recurring-journals-back-button"
        />
        <Title level={3} style={{ margin: 0 }}>Recurring Journals</Title>
      </div>

      <Row
        justify="space-between"
        align="middle"
        style={{ margin: "32px 0" }}
      >
        <Col>
          <Input
            placeholder="Search recurring journals..."
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 250 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col>
          {access_type === "WRITE" && (
           <Button
  type="primary"
  icon={<PlusOutlined />}
  data-testid="btn-add-new-recurring-journal"
  onClick={() => {
    dispatch(setSelectedRecurring(null));
    dispatch(setIsEditRecurring(false));
    navigate("/accounts/recurring-journals/add");
  }}
>
  Add New
</Button>
          )}
        </Col>
      </Row>

      <Table
        bordered
        loading={loading}
        columns={columns}
        dataSource={filtered}
        rowKey="id"
        pagination={{
          current:      currentPage,
          pageSize:     pageSize,
          total:        filtered.length,
          showQuickJumper: true,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page) => setCurrentPage(page),
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }}
        size="middle"
        scroll={{ x: "max-content" }}
        style={{
          border:          "1px solid #f0f0f0",
          borderRadius:    8,
          overflow:        "hidden",
          backgroundColor: "#fff",
        }}
        expandable={{
          expandedRowRender: (record) => (
            <Table
              rowKey={(r, i) => i}
              columns={detailColumns}
              dataSource={record.rows_json || []}
              pagination={false}
              size="small"
              bordered
            />
          ),
        }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
      />
    </>
  );
};

export default RecurringJournalList;